import { BarChart3, RefreshCw, ShoppingBag, CreditCard, Package, RotateCcw, ArrowRight, Clock } from "lucide-react";
import { FeaturePageLayout } from "../FeaturePageLayout";
import { PageMeta } from "@/components/seo/PageMeta";
import { useAuthDialog } from "@/lib/auth-dialog-context";

const METRICS = [
  { icon: ShoppingBag, label: "Today's Orders", value: "47", sub: "+12% vs yesterday", tone: "bg-purple-50 text-purple-700" },
  { icon: BarChart3, label: "Revenue", value: "₹38,200", sub: "↑ ₹4,100 today", tone: "bg-emerald-50 text-emerald-700" },
  { icon: Package, label: "COD Orders", value: "29", sub: "62% of total", tone: "bg-amber-50 text-amber-700" },
  { icon: CreditCard, label: "Prepaid", value: "18", sub: "38% of total", tone: "bg-violet-50 text-violet-700" },
  { icon: RotateCcw, label: "Returns / RTO", value: "2", sub: "4% return rate", tone: "bg-slate-100 text-slate-700" },
  { icon: Clock, label: "Best Hour", value: "11 AM", sub: "Peak sales time", tone: "bg-rose-50 text-rose-700" },
];

const BENEFITS = [
  {
    icon: RefreshCw,
    title: "One-click order sync",
    desc: "Hit Sync and EasyLister pulls your latest orders from your seller panel. Live status so you always know the data is fresh.",
  },
  {
    icon: BarChart3,
    title: "Revenue, COD & Prepaid split",
    desc: "See your daily revenue alongside COD vs prepaid breakdown. Know exactly how much cash is coming in vs. how much is locked in transit.",
  },
  {
    icon: ShoppingBag,
    title: "Cancellations & RTO tracking",
    desc: "Monitor cancellations and return-to-origin orders alongside your success metrics. Spot trends before they dent your seller rating.",
  },
  {
    icon: Clock,
    title: "Best hour & average order value",
    desc: "Identify your peak sales hour and track your average order value. Use this to time flash sales and promotions for maximum impact.",
  },
];

export const DashboardLandingPage = () => {
  const { openAuth } = useAuthDialog();

  return (
    <FeaturePageLayout currentPath="/dashboard">
      <PageMeta
        title="Seller Sales Dashboard — Track Orders, Revenue & COD/Prepaid Split"
        description="Real-time daily orders, revenue, COD vs prepaid split, and RTO tracking for Meesho & Flipkart sellers."
        keywords="meesho seller dashboard, meesho daily orders, meesho revenue tracker, COD prepaid split meesho, flipkart seller dashboard, seller sales report india"
        path="/dashboard"
        ogTitle="Seller Dashboard — Orders, Revenue & COD/Prepaid | EasyLister"
        ogDescription="Track daily orders, revenue, COD vs prepaid, returns, and peak sales hours for Meesho & Flipkart. One-click sync."
        twitterTitle="Meesho & Flipkart seller dashboard — real-time daily report"
        twitterDescription="Orders, revenue, COD/prepaid split, RTO, best hour — one sync, full picture."
      />

      {/* Hero */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider mb-6">
              EasyLister · Sales Dashboard
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight">
              Track Orders, Revenue &{" "}
              <span className="bg-gradient-to-br from-purple-700 to-violet-600 bg-clip-text text-transparent">
                COD/Prepaid Split
              </span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
              Stop navigating four different seller panel pages to understand your day. EasyLister's
              dashboard pulls your Meesho and Flipkart data into a single view — orders, revenue, COD
              vs prepaid, cancellations, RTO, and your best sales hour — with one click.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <button
                onClick={() => openAuth("signup")}
                className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-full shadow-md hover:shadow-lg transition-all text-sm"
              >
                Try Free — No Card Needed <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Metrics mockup */}
      <section className="py-16 bg-slate-50 border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-6">
            What you see at a glance
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {METRICS.map((m) => (
              <div
                key={m.label}
                className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm"
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${m.tone}`}>
                  <m.icon className="w-4.5 h-4.5" />
                </div>
                <div className="font-heading text-xl font-extrabold text-slate-900">{m.value}</div>
                <div className="text-xs font-semibold text-slate-500 mt-0.5">{m.label}</div>
                <div className="text-[11px] text-slate-400 mt-1">{m.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900 mb-12">
            Know your business numbers without the tab-hopping
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {BENEFITS.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="p-6 rounded-2xl border border-slate-100 bg-slate-50 hover:border-purple-200 hover:bg-purple-50/30 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading font-bold text-slate-900 mb-2">{title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms */}
      <section className="py-14 bg-gradient-to-b from-white to-purple-50/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-xl font-extrabold text-slate-900 mb-3">
            Works with your marketplaces
          </h2>
          <p className="text-slate-600 text-sm mb-8 max-w-xl mx-auto">
            Connect data from Meesho and Flipkart. More marketplaces coming soon.
          </p>
          <div className="flex justify-center gap-6 flex-wrap">
            {["Meesho", "Flipkart"].map((p) => (
              <span
                key={p}
                className="px-5 py-2.5 rounded-full border border-purple-200 bg-white text-sm font-bold text-slate-800 shadow-sm"
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>
    </FeaturePageLayout>
  );
};
